import {
    type GestureResponderEvent,
    Platform,
    Linking
} from "react-native";
import type {
    MarkdownBlockParserStateType,
    MarkdownLinkDestinationType,
    MarkdownSafeUrlConstantType,
    MarkdownListStackItemType,
    MarkdownListPositionType,
    MarkdownInlineMatchType,
    MarkdownInlineTokenType,
    MarkdownListGroupType,
    MarkdownLinkMatchType,
    MarkdownInlineObject,
    MarkdownFenceType,
    MarkdownObject,
    MarkdownSyntax,
    ListTypes
} from "./type";
import {
    MARKDOWN_BARE_URL_TRAILING_CHARACTERS,
    MARKDOWN_MAX_LINK_DESTINATION_LENGTH,
    MARKDOWN_URL_TAB_OR_NEWLINE_PATTERN,
    MARKDOWN_SETEXT_UNDERLINE_PATTERN,
    MARKDOWN_SAFE_IMAGE_DATA_PATTERN,
    MARKDOWN_URL_BOUNDARY_CHARACTERS,
    MARKDOWN_EMAIL_AUTOLINK_PATTERN,
    MARKDOWN_THEMATIC_BREAK_PATTERN,
    MARKDOWN_PARAGRAPH_TAG_PATTERN,
    MARKDOWN_MAX_LINK_LABEL_LENGTH,
    MARKDOWN_ESCAPABLE_CHARACTERS,
    MARKDOWN_PUNCTUATION_PATTERN,
    MARKDOWN_ATX_HEADING_PATTERN,
    MARKDOWN_WHITESPACE_PATTERN,
    MARKDOWN_UNSAFE_URL_PATTERN,
    MARKDOWN_BLOCKQUOTE_PATTERN,
    MARKDOWN_BREAK_LINE_PATTERN,
    MARKDOWN_LIST_ITEM_PATTERN,
    MARKDOWN_BREAK_TAG_PATTERN,
    MARKDOWN_HTML_INLINE_TAGS,
    MARKDOWN_AUTOLINK_PATTERN,
    MARKDOWN_HTML_TAG_PATTERN,
    MARKDOWN_BARE_URL_PATTERN,
    MARKDOWN_FALLBACK_VARIANT,
    MARKDOWN_ENTITY_PATTERN,
    MARKDOWN_URL_SPACE_CODE,
    MINOR_HEADING_VARIANTS,
    MARKDOWN_FENCE_PATTERN,
    MARKDOWN_HTML_ENTITIES,
    MARKDOWN_HEADING_KEYS,
    LEGACY_INLINE_PATTERN,
    MARKDOWN_MAX_NESTING,
    LEGACY_IMAGE_PATTERN,
    DEFAULT_VARIANTS
} from "./constant";

export const getMarkdownDefaultVariant = (nativeType: MarkdownObject["nativeType"]): keyof NCoreUIKit.Typography => {
    const key = nativeType as keyof typeof DEFAULT_VARIANTS;

    return DEFAULT_VARIANTS[key] ?? MINOR_HEADING_VARIANTS[key] ?? MARKDOWN_FALLBACK_VARIANT;
};

export const imageMarkdown = ({
    index,
    line
}: {
    index: number;
    line: string;
}): MarkdownObject => {
    const contentMatch = line.match(/\[(.*?)\]/);
    const imageContent = (contentMatch && contentMatch[1]) ? contentMatch[1] : "";
    const tmpLine = line.replace(/\[.*?\]/g, "");

    const urlMatch = tmpLine.match(/\((.*?)\)/);
    const imageURL = (urlMatch && urlMatch[1]) ? urlMatch[1] : "";
    const tmpLine2 = tmpLine.replace(/\((.*?)\)/, "");

    const imageSizeMatch = tmpLine2.match(/<(.*?)>/);
    const imageSize = (imageSizeMatch && imageSizeMatch[1]) ? imageSizeMatch[1].split("x").map(e => {
        return e.indexOf("%") !== -1 || e.indexOf("auto") !== -1 ? e : Number(e);
    }) : undefined;
    const tmpLine3 = tmpLine2.replace(/<(.*?)>/, "");

    const resizeModeMatch = tmpLine3.match(/--(.*?)--/);
    const resizeMode = (resizeModeMatch && resizeModeMatch[1]) ? resizeModeMatch[1] as "cover" | "contain" | "center" | "none" | "repeat" | "stretch" : undefined;

    return {
        resizeMode: resizeMode,
        content: imageContent,
        nativeType: "image",
        size: imageSize,
        type: "image",
        url: imageURL,
        key: index
    };
};

export const textMarkdown = ({
    index,
    type,
    line
}: {
    type: "# " | "## " | "### " | "#### " | "##### " | "###### " | "<p>";
    index: number;
    line: string;
}): MarkdownObject => {
    let content = line.replace(type, "");
    if (type === "<p>") {
        content = content.replace("</p>", "");
    }

    return {
        variant: getMarkdownDefaultVariant(type),
        nativeType: type,
        type: "text",
        key: index,
        content
    };
};

export const enterMarkdown = ({
    index,
    line
}: {
    index: number;
    line: string;
}): MarkdownObject => {
    const _line = line.replace("<br/>", "");

    return {
        variant: DEFAULT_VARIANTS["<br/>"],
        nativeType: "<br/>",
        content: _line,
        type: "space",
        key: index
    };
};

export const codeMarkdown = ({
    skippedIndices,
    index,
    lines
}: {
    skippedIndices: number[];
    lines: string[];
    index: number;
}): MarkdownObject | void => {
    let endIndex = -1;

    for (let j = index + 1; j < lines.length; j++) {
        if (lines[j]?.startsWith("```")) {
            endIndex = j;
            break;
        }
    }

    if (endIndex !== -1) {
        for (let k = index + 1; k <= endIndex; k++) {
            skippedIndices.push(k);
        }

        const codeContent = lines.slice(index + 1, endIndex).join("\n");

        const languageMatch = lines[index]?.match(/^```(.*)/);
        const language = languageMatch && languageMatch[1] ? languageMatch[1].trim() : "";

        return {
            language: language !== "" ? language : undefined,
            variant: DEFAULT_VARIANTS["```"],
            content: codeContent,
            nativeType: "```",
            type: "code",
            key: index
        };
    }
};

export const centerMarkdown = ({
    skippedIndices,
    index,
    lines
}: {
    skippedIndices: number[];
    lines: string[];
    index: number;
}): MarkdownObject | void => {
    let endIndex = -1;

    for (let j = index + 1; j < lines.length; j++) {
        if (lines[j]?.startsWith("</center>")) {
            endIndex = j;
            break;
        }
    }

    if (endIndex !== -1) {
        for (let k = index + 1; k <= endIndex; k++) {
            skippedIndices.push(k);
        }

        const centerContent = lines.slice(index + 1, endIndex).join("\n");

        return {
            content: centerContent,
            nativeType: "center",
            type: "center",
            key: index
        };
    }
};

export const leftMarkdown = ({
    skippedIndices,
    index,
    lines
}: {
    skippedIndices: number[];
    lines: string[];
    index: number;
}): MarkdownObject | void => {
    let endIndex = -1;

    for (let j = index + 1; j < lines.length; j++) {
        if (lines[j]?.startsWith("</left>")) {
            endIndex = j;
            break;
        }
    }

    if (endIndex !== -1) {
        for (let k = index + 1; k <= endIndex; k++) {
            skippedIndices.push(k);
        }

        const leftContent = lines.slice(index + 1, endIndex).join("\n");

        return {
            content: leftContent,
            nativeType: "left",
            type: "left",
            key: index
        };
    }
};

export const rightMarkdown = ({
    skippedIndices,
    index,
    lines
}: {
    skippedIndices: number[];
    lines: string[];
    index: number;
}): MarkdownObject | void => {
    let endIndex = -1;

    for (let j = index + 1; j < lines.length; j++) {
        if (lines[j]?.startsWith("</right>")) {
            endIndex = j;
            break;
        }
    }

    if (endIndex !== -1) {
        for (let k = index + 1; k <= endIndex; k++) {
            skippedIndices.push(k);
        }

        const rightContent = lines.slice(index + 1, endIndex).join("\n");

        return {
            content: rightContent,
            nativeType: "right",
            type: "right",
            key: index
        };
    }
};

export const linkMarkdown = ({
    linkStartRegex,
    skippedIndices,
    index,
    lines,
    line
}: {
    skippedIndices: number[];
    linkStartRegex: RegExp;
    lines: string[];
    index: number;
    line: string;
}): MarkdownObject | void => {
    let endIndex = -1;

    for (let j = index + 1; j < lines.length; j++) {
        if (lines[j]?.startsWith("</link>")) {
            endIndex = j;
            break;
        }
    }

    if (endIndex !== -1) {
        const match = line.match(linkStartRegex);
        const href = match ? match[1] : "";

        for (let k = index + 1; k <= endIndex; k++) {
            skippedIndices.push(k);
        }

        const linkContent = lines.slice(index + 1, endIndex).join("\n");

        return {
            content: linkContent,
            nativeType: "link",
            type: "link",
            key: index,
            url: href
        };
    }
};

export const blockquoteMarkdown = ({
    skippedIndices,
    index,
    lines
}: {
    skippedIndices: number[];
    lines: string[];
    index: number;
}): MarkdownObject | void => {
    let endIndex = -1;

    for (let j = index + 1; j < lines.length; j++) {
        if (lines[j]?.startsWith(">>")) {
            endIndex = j;
            break;
        }
    }

    if (endIndex !== -1) {
        for (let k = index + 1; k <= endIndex; k++) {
            skippedIndices.push(k);
        }

        const quoteContent = lines.slice(index + 1, endIndex).join("\n");

        return {
            variant: DEFAULT_VARIANTS["<<"],
            content: quoteContent,
            type: "blockquote",
            nativeType: "<<",
            key: index
        };
    }
};

export const listMarkdown = ({
    isStarList,
    indent,
    index,
    line
}: {
    isStarList: boolean;
    indent: number;
    index: number;
    line: string;
}): MarkdownObject => {
    return {
        variant: DEFAULT_VARIANTS[isStarList ? "* " : "- "],
        nativeType: isStarList ? "* " : "- ",
        content: line,
        type: "list",
        key: index,
        indent
    };
};

const getMarkdownNormalizedUrl = (url: string): string => {
    let start = 0;

    while(start < url.length && url.charCodeAt(start) <= MARKDOWN_URL_SPACE_CODE) {
        start++;
    }

    return url.slice(start).replace(MARKDOWN_URL_TAB_OR_NEWLINE_PATTERN, "").toLowerCase();
};

export const getMarkdownSafeUrl = ({
    isImage = false,
    url
}: MarkdownSafeUrlConstantType): string | undefined => {
    const trimmedUrl = url ? url.trim() : "";

    if(!trimmedUrl) {
        return undefined;
    }

    const normalizedUrl = getMarkdownNormalizedUrl(trimmedUrl);

    if(isImage && MARKDOWN_SAFE_IMAGE_DATA_PATTERN.test(normalizedUrl)) {
        return trimmedUrl;
    }

    if(MARKDOWN_UNSAFE_URL_PATTERN.test(normalizedUrl)) {
        return undefined;
    }

    return trimmedUrl;
};

export const getMarkdownPlainText = (nodes: Array<MarkdownInlineObject>): string => {
    return nodes.map((node) => {
        if(node.children) {
            return getMarkdownPlainText(node.children);
        }

        return node.content;
    }).join("");
};

const MARKDOWN_NODE_DEPTHS = new WeakMap<MarkdownInlineObject, number>();

const getMarkdownNodeDepth = (node: MarkdownInlineObject): number => {
    return MARKDOWN_NODE_DEPTHS.get(node) ?? 0;
};

const getMarkdownChildrenDepth = (children: Array<MarkdownInlineObject>): number => {
    return children.reduce((deepest, child) => Math.max(deepest, getMarkdownNodeDepth(child)), 0) + 1;
};

const getMarkdownHasLink = (nodes: Array<MarkdownInlineObject>): boolean => {
    return nodes.some((node) => node.type === "link" || (!!node.children && getMarkdownHasLink(node.children)));
};

const getMarkdownIndent = (line: string): number => {
    let indent = 0;

    for(const character of line) {
        if(character === " ") {
            indent++;

            continue;
        }

        if(character === "\t") {
            indent += 4 - (indent % 4);

            continue;
        }

        break;
    }

    return indent;
};

const removeMarkdownIndent = ({
    indent,
    line
}: {
    indent: number;
    line: string;
}): string => {
    let index = 0;

    while(index < indent && line.charAt(index) === " ") {
        index++;
    }

    return line.slice(index);
};

const trimMarkdownEnd = ({
    characters,
    text
}: {
    characters: string;
    text: string;
}): string => {
    let end = text.length;

    while(end > 0 && characters.includes(text.charAt(end - 1))) {
        end--;
    }

    return text.slice(0, end);
};

const getMarkdownHeadingContent = (text: string): string => {
    const trimmedText = text.trim();

    const contentText = trimMarkdownEnd({
        text: trimmedText,
        characters: "#"
    });

    if(contentText.length === trimmedText.length) {
        return trimmedText;
    }

    if(!contentText) {
        return "";
    }

    const closingCharacter = contentText.charAt(contentText.length - 1);

    if(closingCharacter !== " " && closingCharacter !== "\t") {
        return trimmedText;
    }

    return contentText.trim();
};

const unescapeMarkdown = (text: string): string => {
    let result = "";

    let index = 0;

    while(index < text.length) {
        const character = text.charAt(index);

        const nextCharacter = text.charAt(index + 1);

        if(character === "\\" && nextCharacter && MARKDOWN_ESCAPABLE_CHARACTERS.includes(nextCharacter)) {
            result = `${result}${nextCharacter}`;

            index += 2;

            continue;
        }

        result = `${result}${character}`;

        index++;
    }

    return result;
};

const decodeMarkdownEntity = (entity: string): string | undefined => {
    if(!entity.startsWith("#")) {
        return MARKDOWN_HTML_ENTITIES[entity];
    }

    const isHex = entity.charAt(1) === "x" || entity.charAt(1) === "X";

    const codePoint = isHex ? parseInt(entity.slice(2), 16) : parseInt(entity.slice(1), 10);

    if(!codePoint || codePoint > 0x10FFFF || (codePoint >= 0xD800 && codePoint <= 0xDFFF)) {
        return "\uFFFD";
    }

    return String.fromCodePoint(codePoint);
};

const pushMarkdownText = ({
    content,
    tokens
}: {
    tokens: Array<MarkdownInlineTokenType>;
    content: string;
}) => {
    const lastNode = tokens[tokens.length - 1]?.node;

    if(lastNode && lastNode.type === "text") {
        lastNode.content = `${lastNode.content}${content}`;

        return;
    }

    tokens.push({
        node: {
            type: "text",
            content
        }
    });
};

const findMarkdownBacktickRun = ({
    length,
    start,
    text
}: {
    length: number;
    start: number;
    text: string;
}): number => {
    let index = text.indexOf("`", start);

    while(index !== -1) {
        let end = index;

        while(text.charAt(end) === "`") {
            end++;
        }

        if(end - index === length) {
            return index;
        }

        index = text.indexOf("`", end);
    }

    return -1;
};

const skipMarkdownSpaces = ({
    index,
    text
}: {
    index: number;
    text: string;
}): number => {
    let currentIndex = index;

    while(currentIndex < text.length && MARKDOWN_WHITESPACE_PATTERN.test(text.charAt(currentIndex))) {
        currentIndex++;
    }

    return currentIndex;
};

const matchMarkdownLinkDestination = ({
    start,
    text
}: {
    start: number;
    text: string;
}): MarkdownLinkDestinationType | null => {
    let index = skipMarkdownSpaces({
        index: start,
        text
    });

    let url = "";

    const isDataUrl = text.slice(index, index + 5).toLowerCase() === "data:";

    const maxLength = isDataUrl ? Infinity : MARKDOWN_MAX_LINK_DESTINATION_LENGTH;

    if(text.charAt(index) === "<") {
        const closeIndex = text.indexOf(">", index + 1);

        if(closeIndex === -1 || closeIndex - index > maxLength) {
            return null;
        }

        url = text.slice(index + 1, closeIndex);

        if(url.includes("\n") || url.includes("<")) {
            return null;
        }

        index = closeIndex + 1;
    } else {
        const urlStart = index;

        let depth = 0;

        while(index < text.length && index - urlStart <= maxLength) {
            const character = text.charAt(index);

            if(character === "\\" && MARKDOWN_ESCAPABLE_CHARACTERS.includes(text.charAt(index + 1))) {
                index += 2;

                continue;
            }

            if(MARKDOWN_WHITESPACE_PATTERN.test(character)) {
                break;
            }

            if(character === "(") {
                depth++;
            }

            if(character === ")") {
                if(depth === 0) {
                    break;
                }

                depth--;
            }

            index++;
        }

        if(index - urlStart > maxLength) {
            return null;
        }

        url = text.slice(urlStart, index);
    }

    let afterUrl = skipMarkdownSpaces({
        index,
        text
    });

    let title: string | undefined;

    const titleOpener = text.charAt(afterUrl);

    if(afterUrl > index && (titleOpener === "\"" || titleOpener === "'" || titleOpener === "(")) {
        const titleCloser = titleOpener === "(" ? ")" : titleOpener;

        let titleIndex = afterUrl + 1;

        while(titleIndex < text.length && text.charAt(titleIndex) !== titleCloser) {
            titleIndex += text.charAt(titleIndex) === "\\" ? 2 : 1;
        }

        if(titleIndex >= text.length) {
            return null;
        }

        title = unescapeMarkdown(text.slice(afterUrl + 1, titleIndex));

        afterUrl = skipMarkdownSpaces({
            index: titleIndex + 1,
            text
        });
    }

    if(text.charAt(afterUrl) !== ")") {
        return null;
    }

    return {
        url: unescapeMarkdown(url),
        end: afterUrl + 1,
        title
    };
};

const matchMarkdownLink = ({
    start,
    text
}: {
    start: number;
    text: string;
}): MarkdownLinkMatchType | null => {
    let closeIndex = -1;

    let depth = 0;

    let index = start;

    while(index < text.length && index - start <= MARKDOWN_MAX_LINK_LABEL_LENGTH) {
        const character = text.charAt(index);

        if(character === "\\") {
            index += 2;

            continue;
        }

        if(character === "`") {
            let runEnd = index;

            while(text.charAt(runEnd) === "`") {
                runEnd++;
            }

            const codeEnd = findMarkdownBacktickRun({
                length: runEnd - index,
                start: runEnd,
                text
            });

            index = codeEnd === -1 ? runEnd : codeEnd + runEnd - index;

            continue;
        }

        if(character === "[") {
            depth++;
        }

        if(character === "]") {
            depth--;

            if(depth === 0) {
                closeIndex = index;

                break;
            }
        }

        index++;
    }

    if(closeIndex === -1 || text.charAt(closeIndex + 1) !== "(") {
        return null;
    }

    const destination = matchMarkdownLinkDestination({
        start: closeIndex + 2,
        text
    });

    if(!destination) {
        return null;
    }

    return {
        label: text.slice(start + 1, closeIndex),
        title: destination.title,
        end: destination.end,
        url: destination.url
    };
};

const trimMarkdownBareUrl = (url: string): string => {
    let openCount = 0;

    let closeCount = 0;

    for(const character of url) {
        if(character === "(") {
            openCount++;
        }

        if(character === ")") {
            closeCount++;
        }
    }

    let end = url.length;

    while(end > 0) {
        const lastCharacter = url.charAt(end - 1);

        if(MARKDOWN_BARE_URL_TRAILING_CHARACTERS.includes(lastCharacter)) {
            end--;

            continue;
        }

        if(lastCharacter === ")" && openCount < closeCount) {
            closeCount--;

            end--;

            continue;
        }

        break;
    }

    return url.slice(0, end);
};

const matchMarkdownHtml = ({
    isInsideLink,
    depth,
    index,
    text
}: {
    isInsideLink: boolean;
    index: number;
    depth: number;
    text: string;
}): MarkdownInlineMatchType | null => {
    const rest = text.slice(index);

    const breakMatch = rest.match(MARKDOWN_BREAK_TAG_PATTERN);

    if(breakMatch) {
        return {
            end: index + breakMatch[0].length,
            node: {
                type: "break",
                content: "\n"
            }
        };
    }

    const autolinkMatch = rest.match(MARKDOWN_AUTOLINK_PATTERN) ?? rest.match(MARKDOWN_EMAIL_AUTOLINK_PATTERN);

    if(autolinkMatch && autolinkMatch[1]) {
        const label = autolinkMatch[1];

        const isEmail = !MARKDOWN_AUTOLINK_PATTERN.test(rest);

        return {
            node: {
                url: isEmail ? `mailto:${label}` : label,
                children: [
                    {
                        content: label,
                        type: "text"
                    }
                ],
                content: label,
                type: "link"
            },
            end: index + autolinkMatch[0].length
        };
    }

    const tagMatch = rest.match(MARKDOWN_HTML_TAG_PATTERN);

    if(!tagMatch || !tagMatch[1]) {
        return null;
    }

    const tagName = tagMatch[1].toLowerCase();

    const closeTag = `</${tagName}>`;

    const closeIndex = rest.toLowerCase().indexOf(closeTag, tagMatch[0].length);

    const tagType = MARKDOWN_HTML_INLINE_TAGS[tagName];

    if(closeIndex === -1 || !tagType) {
        return null;
    }

    const children = parseStandardInline(rest.slice(tagMatch[0].length, closeIndex), depth + 1, isInsideLink);

    const node: MarkdownInlineObject = {
        content: getMarkdownPlainText(children),
        type: tagType,
        children
    };

    MARKDOWN_NODE_DEPTHS.set(node, getMarkdownChildrenDepth(children));

    return {
        end: index + closeIndex + closeTag.length,
        node
    };
};

const matchMarkdownBareUrl = ({
    index,
    text
}: {
    index: number;
    text: string;
}): MarkdownInlineMatchType | null => {
    const previousCharacter = index > 0 ? text.charAt(index - 1) : " ";

    if(!MARKDOWN_WHITESPACE_PATTERN.test(previousCharacter) && !MARKDOWN_URL_BOUNDARY_CHARACTERS.includes(previousCharacter)) {
        return null;
    }

    const urlMatch = text.slice(index).match(MARKDOWN_BARE_URL_PATTERN);

    if(!urlMatch) {
        return null;
    }

    const url = trimMarkdownBareUrl(urlMatch[0]);

    if(!MARKDOWN_BARE_URL_PATTERN.test(url) || url.endsWith("//")) {
        return null;
    }

    return {
        node: {
            children: [
                {
                    content: url,
                    type: "text"
                }
            ],
            content: url,
            type: "link",
            url
        },
        end: index + url.length
    };
};

const getMarkdownDelimiterToken = ({
    character,
    index,
    text,
    end
}: {
    character: "*" | "_" | "~";
    index: number;
    text: string;
    end: number;
}): MarkdownInlineTokenType => {
    const previousCharacter = index > 0 ? text.charAt(index - 1) : "\n";

    const nextCharacter = end < text.length ? text.charAt(end) : "\n";

    const isPreviousWhitespace = MARKDOWN_WHITESPACE_PATTERN.test(previousCharacter);

    const isNextWhitespace = MARKDOWN_WHITESPACE_PATTERN.test(nextCharacter);

    const isPreviousPunctuation = MARKDOWN_PUNCTUATION_PATTERN.test(previousCharacter);

    const isNextPunctuation = MARKDOWN_PUNCTUATION_PATTERN.test(nextCharacter);

    const isLeftFlanking = !isNextWhitespace && (!isNextPunctuation || isPreviousWhitespace || isPreviousPunctuation);

    const isRightFlanking = !isPreviousWhitespace && (!isPreviousPunctuation || isNextWhitespace || isNextPunctuation);

    const isUnderscore = character === "_";

    return {
        delimiter: {
            isOpener: isUnderscore ? isLeftFlanking && (!isRightFlanking || isPreviousPunctuation) : isLeftFlanking,
            isCloser: isUnderscore ? isRightFlanking && (!isLeftFlanking || isNextPunctuation) : isRightFlanking,
            originalLength: end - index,
            length: end - index,
            character
        }
    };
};

const flattenMarkdownTokens = (tokens: Array<MarkdownInlineTokenType>): Array<MarkdownInlineObject> => {
    const nodes: Array<MarkdownInlineObject> = [];

    tokens.forEach((token) => {
        const content = token.delimiter ? token.delimiter.character.repeat(token.delimiter.length) : token.node?.type === "text" ? token.node.content : "";

        const lastNode = nodes[nodes.length - 1];

        if((token.delimiter || token.node?.type === "text") && lastNode && lastNode.type === "text") {
            lastNode.content = `${lastNode.content}${content}`;

            return;
        }

        if(token.delimiter) {
            if(content) {
                nodes.push({
                    type: "text",
                    content
                });
            }

            return;
        }

        if(token.node) {
            nodes.push(token.node.type === "text" ? {
                ...token.node
            } : token.node);
        }
    });

    return nodes.filter((node) => node.type !== "text" || node.content !== "");
};

const findMarkdownOpenerIndex = ({
    closerIndex,
    tokens
}: {
    tokens: Array<MarkdownInlineTokenType>;
    closerIndex: number;
}): number => {
    const closer = tokens[closerIndex]?.delimiter;

    if(!closer) {
        return -1;
    }

    for(let openerIndex = closerIndex - 1; openerIndex >= 0; openerIndex--) {
        const opener = tokens[openerIndex]?.delimiter;

        if(!opener || opener.character !== closer.character || !opener.isOpener || opener.length === 0) {
            continue;
        }

        if(closer.character === "~") {
            if(opener.length === closer.length) {
                return openerIndex;
            }

            continue;
        }

        const isOddMatch = (opener.isCloser || closer.isOpener) && (opener.originalLength + closer.originalLength) % 3 === 0 && !(opener.originalLength % 3 === 0 && closer.originalLength % 3 === 0);

        if(!isOddMatch) {
            return openerIndex;
        }
    }

    return -1;
};

const processMarkdownEmphasis = (tokens: Array<MarkdownInlineTokenType>): Array<MarkdownInlineObject> => {
    let closerIndex = 0;

    while(closerIndex < tokens.length) {
        const closer = tokens[closerIndex]?.delimiter;

        if(!closer || !closer.isCloser || closer.length === 0) {
            closerIndex++;

            continue;
        }

        const openerIndex = findMarkdownOpenerIndex({
            closerIndex,
            tokens
        });

        const opener = tokens[openerIndex]?.delimiter;

        if(openerIndex === -1 || !opener) {
            closerIndex++;

            continue;
        }

        const isStrikethrough = closer.character === "~";

        const usedLength = isStrikethrough ? closer.length : opener.length >= 2 && closer.length >= 2 ? 2 : 1;

        const innerTokens = tokens.slice(openerIndex + 1, closerIndex);

        const children = flattenMarkdownTokens(innerTokens);

        const depth = getMarkdownChildrenDepth(children);

        if(depth > MARKDOWN_MAX_NESTING) {
            closerIndex++;

            continue;
        }

        const node: MarkdownInlineObject = {
            type: isStrikethrough ? "strikethrough" : usedLength === 2 ? "strong" : "emphasis",
            content: getMarkdownPlainText(children),
            children
        };

        MARKDOWN_NODE_DEPTHS.set(node, depth);

        tokens.splice(openerIndex + 1, innerTokens.length, {
            node
        });

        opener.length -= usedLength;

        closer.length -= usedLength;

        closerIndex = openerIndex + 2;

        if(opener.length === 0) {
            tokens.splice(openerIndex, 1);

            closerIndex--;
        }

        if(closer.length === 0) {
            tokens.splice(closerIndex, 1);
        }
    }

    return flattenMarkdownTokens(tokens);
};

const parseStandardInline = (text: string, depth: number = 0, isInsideLink: boolean = false): Array<MarkdownInlineObject> => {
    if(depth >= MARKDOWN_MAX_NESTING) {
        return [
            {
                content: text,
                type: "text"
            }
        ];
    }

    const tokens: Array<MarkdownInlineTokenType> = [];

    let index = 0;

    while(index < text.length) {
        const character = text.charAt(index);

        if(character === "\\") {
            const nextCharacter = text.charAt(index + 1);

            if(nextCharacter === "\n") {
                tokens.push({
                    node: {
                        type: "break",
                        content: "\n"
                    }
                });

                index = skipMarkdownSpaces({
                    index: index + 2,
                    text
                });

                continue;
            }

            pushMarkdownText({
                content: nextCharacter && MARKDOWN_ESCAPABLE_CHARACTERS.includes(nextCharacter) ? nextCharacter : character,
                tokens
            });

            index += nextCharacter && MARKDOWN_ESCAPABLE_CHARACTERS.includes(nextCharacter) ? 2 : 1;

            continue;
        }

        if(character === "\n") {
            const lastNode = tokens[tokens.length - 1]?.node;

            let isHardBreak = false;

            if(lastNode && lastNode.type === "text") {
                const trimmedContent = trimMarkdownEnd({
                    text: lastNode.content,
                    characters: " "
                });

                isHardBreak = lastNode.content.length - trimmedContent.length >= 2;

                lastNode.content = trimmedContent;
            }

            let nextIndex = index + 1;

            while(text.charAt(nextIndex) === " " || text.charAt(nextIndex) === "\t") {
                nextIndex++;
            }

            if(text.charAt(nextIndex) === "\n") {
                tokens.push({
                    node: {
                        type: "break",
                        content: "\n"
                    }
                }, {
                    node: {
                        type: "break",
                        content: "\n"
                    }
                });

                index = skipMarkdownSpaces({
                    index: nextIndex,
                    text
                });

                continue;
            }

            if(isHardBreak) {
                tokens.push({
                    node: {
                        type: "break",
                        content: "\n"
                    }
                });
            } else {
                pushMarkdownText({
                    content: " ",
                    tokens
                });
            }

            index = nextIndex;

            continue;
        }

        if(character === "`") {
            let runEnd = index;

            while(text.charAt(runEnd) === "`") {
                runEnd++;
            }

            const codeEnd = findMarkdownBacktickRun({
                length: runEnd - index,
                start: runEnd,
                text
            });

            if(codeEnd === -1) {
                pushMarkdownText({
                    content: text.slice(index, runEnd),
                    tokens
                });

                index = runEnd;

                continue;
            }

            const rawCode = text.slice(runEnd, codeEnd).replace(/\n/g, " ");

            const isPadded = rawCode.length > 2 && rawCode.startsWith(" ") && rawCode.endsWith(" ") && rawCode.trim() !== "";

            tokens.push({
                node: {
                    content: isPadded ? rawCode.slice(1, -1) : rawCode,
                    type: "code"
                }
            });

            index = codeEnd + runEnd - index;

            continue;
        }

        if(character === "*" || character === "_" || character === "~") {
            let runEnd = index;

            while(text.charAt(runEnd) === character) {
                runEnd++;
            }

            if(character === "~" && runEnd - index !== 2) {
                pushMarkdownText({
                    content: text.slice(index, runEnd),
                    tokens
                });

                index = runEnd;

                continue;
            }

            tokens.push(getMarkdownDelimiterToken({
                end: runEnd,
                character,
                index,
                text
            }));

            index = runEnd;

            continue;
        }

        if(character === "<") {
            const htmlMatch = matchMarkdownHtml({
                isInsideLink,
                depth,
                index,
                text
            });

            if(htmlMatch) {
                tokens.push({
                    node: htmlMatch.node
                });

                index = htmlMatch.end;

                continue;
            }
        }

        if(character === "[" || (character === "!" && text.charAt(index + 1) === "[")) {
            const isImage = character === "!";

            const linkMatch = matchMarkdownLink({
                start: isImage ? index + 1 : index,
                text
            });

            if(linkMatch) {
                const children = parseStandardInline(linkMatch.label, depth + 1, true);

                if(!isImage && getMarkdownHasLink(children)) {
                    pushMarkdownText({
                        content: character,
                        tokens
                    });

                    children.forEach((child) => {
                        if(child.type === "text") {
                            pushMarkdownText({
                                content: child.content,
                                tokens
                            });

                            return;
                        }

                        tokens.push({
                            node: child
                        });
                    });

                    pushMarkdownText({
                        content: text.slice(index + 1 + linkMatch.label.length, linkMatch.end),
                        tokens
                    });

                    index = linkMatch.end;

                    continue;
                }

                const content = getMarkdownPlainText(children);

                const node: MarkdownInlineObject = isImage ? {
                    title: linkMatch.title,
                    url: linkMatch.url,
                    type: "image",
                    content
                } : {
                    title: linkMatch.title,
                    url: linkMatch.url,
                    type: "link",
                    children,
                    content
                };

                if(!isImage) {
                    MARKDOWN_NODE_DEPTHS.set(node, getMarkdownChildrenDepth(children));
                }

                tokens.push({
                    node
                });

                index = linkMatch.end;

                continue;
            }
        }

        if(character === "&") {
            const entityMatch = text.slice(index).match(MARKDOWN_ENTITY_PATTERN);

            const decodedEntity = entityMatch && entityMatch[1] ? decodeMarkdownEntity(entityMatch[1]) : undefined;

            if(entityMatch && decodedEntity !== undefined) {
                pushMarkdownText({
                    content: decodedEntity,
                    tokens
                });

                index += entityMatch[0].length;

                continue;
            }
        }

        if(!isInsideLink && (character === "h" || character === "H")) {
            const urlMatch = matchMarkdownBareUrl({
                index,
                text
            });

            if(urlMatch) {
                tokens.push({
                    node: urlMatch.node
                });

                index = urlMatch.end;

                continue;
            }
        }

        pushMarkdownText({
            content: character,
            tokens
        });

        index++;
    }

    const lastNode = tokens[tokens.length - 1]?.node;

    if(depth === 0 && lastNode && lastNode.type === "text") {
        lastNode.content = trimMarkdownEnd({
            text: lastNode.content,
            characters: " \t"
        });
    }

    return processMarkdownEmphasis(tokens);
};

const parseLegacyInline = (text: string): Array<MarkdownInlineObject> => {
    return text.split(LEGACY_INLINE_PATTERN).filter((part) => part !== "").map((part): MarkdownInlineObject => {
        if(part.startsWith("**") && part.endsWith("**")) {
            const content = part.replace(/\*\*/g, "");

            return {
                children: [
                    {
                        type: "text",
                        content
                    }
                ],
                type: "strong",
                content
            };
        }

        if(part.startsWith("__") && part.endsWith("__")) {
            const content = part.replace(/__/g, "");

            return {
                children: [
                    {
                        type: "text",
                        content
                    }
                ],
                type: "emphasis",
                content
            };
        }

        return {
            content: part,
            type: "text"
        };
    });
};

export const parseMarkdownInline = (text: string, syntax: MarkdownSyntax = "standard"): Array<MarkdownInlineObject> => {
    if(syntax === "legacy") {
        return parseLegacyInline(text);
    }

    return parseStandardInline(text);
};

const getMarkdownListPosition = ({
    markerIndent,
    nativeType,
    listStack,
    start
}: {
    listStack: Array<MarkdownListStackItemType>;
    nativeType: ListTypes;
    markerIndent: number;
    start: number;
}): MarkdownListPositionType => {
    let lastItem = listStack[listStack.length - 1];

    while(lastItem && lastItem.markerIndent > markerIndent + 1) {
        listStack.pop();

        lastItem = listStack[listStack.length - 1];
    }

    if(lastItem && markerIndent >= lastItem.markerIndent + 2) {
        const nestedLevel = Math.min(lastItem.level + 1, MARKDOWN_MAX_NESTING);

        listStack.push({
            nextOrder: start + 1,
            level: nestedLevel,
            markerIndent,
            nativeType
        });

        return {
            level: nestedLevel,
            order: start
        };
    }

    if(lastItem && lastItem.nativeType === nativeType) {
        const order = lastItem.nextOrder;

        lastItem.nextOrder = order + 1;

        return {
            level: lastItem.level,
            order
        };
    }

    const level = lastItem ? lastItem.level : 0;

    if(lastItem) {
        listStack.pop();
    }

    listStack.push({
        nextOrder: start + 1,
        markerIndent,
        nativeType,
        level
    });

    return {
        order: start,
        level
    };
};

const getMarkdownFence = (line: string): MarkdownFenceType | null => {
    const fenceMatch = line.match(MARKDOWN_FENCE_PATTERN);

    if(!fenceMatch || !fenceMatch[2]) {
        return null;
    }

    const info = fenceMatch[3] ?? "";

    if(fenceMatch[2].startsWith("`") && info.includes("`")) {
        return null;
    }

    return {
        indent: getMarkdownIndent(fenceMatch[1] ?? ""),
        language: info.trim().split(/\s+/)[0] ?? "",
        marker: fenceMatch[2]
    };
};

const getMarkdownIsFenceEnd = ({
    fence,
    line
}: {
    fence: MarkdownFenceType;
    line: string;
}): boolean => {
    const trimmedLine = line.trim();

    if(!trimmedLine.startsWith(fence.marker) || getMarkdownIndent(line) > fence.indent + 3) {
        return false;
    }

    return trimmedLine.split("").every((character) => character === fence.marker.charAt(0));
};

const getMarkdownIsBlockStart = (line: string): boolean => {
    return MARKDOWN_ATX_HEADING_PATTERN.test(line) || MARKDOWN_THEMATIC_BREAK_PATTERN.test(line) || MARKDOWN_BLOCKQUOTE_PATTERN.test(line) || MARKDOWN_LIST_ITEM_PATTERN.test(line) || !!getMarkdownFence(line);
};

const getMarkdownParagraphLine = (line: string): string => {
    const trimmedLine = line.trimStart();

    if(!trimmedLine.startsWith("<p>") && !trimmedLine.trimEnd().endsWith("</p>")) {
        return trimmedLine;
    }

    return trimmedLine.trimEnd().replace(MARKDOWN_PARAGRAPH_TAG_PATTERN, "");
};

const getMarkdownStandaloneImage = (inlines: Array<MarkdownInlineObject>): MarkdownInlineObject | undefined => {
    const visibleInlines = inlines.filter((inline) => inline.type !== "text" || inline.content.trim() !== "");

    if(visibleInlines.length !== 1 || visibleInlines[0]?.type !== "image") {
        return undefined;
    }

    return visibleInlines[0];
};

const closeMarkdownParagraph = (state: MarkdownBlockParserStateType) => {
    const paragraph = state.paragraph;

    if(!paragraph) {
        return;
    }

    state.paragraph = null;

    const content = paragraph.lines.join("\n");

    if(!content.trim()) {
        return;
    }

    const inlines = parseStandardInline(content);

    const image = getMarkdownStandaloneImage(inlines);

    if(image) {
        state.blocks.push({
            content: image.content,
            nativeType: "image",
            key: paragraph.key,
            title: image.title,
            url: image.url,
            type: "image"
        });

        return;
    }

    state.blocks.push({
        variant: getMarkdownDefaultVariant("<p>"),
        key: paragraph.key,
        nativeType: "<p>",
        type: "text",
        content,
        inlines
    });
};

const closeMarkdownList = (state: MarkdownBlockParserStateType) => {
    state.listStack.length = 0;

    state.listItem = null;
};

const closeMarkdownBlocks = (state: MarkdownBlockParserStateType) => {
    closeMarkdownParagraph(state);

    closeMarkdownList(state);
};

const collectMarkdownQuote = ({
    lines,
    start
}: {
    lines: Array<string>;
    start: number;
}): {
    innerLines: Array<string>;
    end: number;
} => {
    const innerLines: Array<string> = [];

    let openFence: MarkdownFenceType | null = null;

    let isLazyAllowed = false;

    let index = start;

    while(index < lines.length) {
        const line = lines[index] ?? "";

        if(MARKDOWN_BLOCKQUOTE_PATTERN.test(line)) {
            const innerLine = line.replace(MARKDOWN_BLOCKQUOTE_PATTERN, "");

            innerLines.push(innerLine);

            let deepLine = innerLine;

            for(let depth = 0; depth < MARKDOWN_MAX_NESTING && MARKDOWN_BLOCKQUOTE_PATTERN.test(deepLine); depth++) {
                deepLine = deepLine.replace(MARKDOWN_BLOCKQUOTE_PATTERN, "");
            }

            if(openFence) {
                if(getMarkdownIsFenceEnd({
                    fence: openFence,
                    line: deepLine
                })) {
                    openFence = null;
                }

                isLazyAllowed = false;
            } else {
                openFence = getMarkdownFence(deepLine);

                isLazyAllowed = !openFence && deepLine.trim() !== "" && !MARKDOWN_ATX_HEADING_PATTERN.test(deepLine) && !MARKDOWN_THEMATIC_BREAK_PATTERN.test(deepLine) && !MARKDOWN_SETEXT_UNDERLINE_PATTERN.test(deepLine);
            }

            index++;

            continue;
        }

        if(isLazyAllowed && line.trim() !== "" && !getMarkdownIsBlockStart(line)) {
            innerLines.push(line);

            index++;

            continue;
        }

        break;
    }

    return {
        end: index,
        innerLines
    };
};

const parseStandardBlocks = ({
    depth = 0,
    offset,
    lines
}: {
    lines: Array<string>;
    offset: number;
    depth?: number;
}): Array<MarkdownObject> => {
    const state: MarkdownBlockParserStateType = {
        isAfterBlank: false,
        paragraph: null,
        listItem: null,
        listStack: [],
        blocks: []
    };

    let index = 0;

    while(index < lines.length) {
        const line = lines[index] ?? "";

        const key = offset + index;

        if(!line.trim()) {
            closeMarkdownParagraph(state);

            state.isAfterBlank = true;

            index++;

            continue;
        }

        const fence = getMarkdownFence(line);

        if(fence) {
            closeMarkdownBlocks(state);

            const codeLines: Array<string> = [];

            let endIndex = index + 1;

            while(endIndex < lines.length && !getMarkdownIsFenceEnd({
                line: lines[endIndex] ?? "",
                fence
            })) {
                codeLines.push(removeMarkdownIndent({
                    line: lines[endIndex] ?? "",
                    indent: fence.indent
                }));

                endIndex++;
            }

            state.blocks.push({
                variant: getMarkdownDefaultVariant("```"),
                language: fence.language || undefined,
                content: codeLines.join("\n"),
                nativeType: "```",
                type: "code",
                key
            });

            state.isAfterBlank = false;

            index = endIndex + 1;

            continue;
        }

        const headingMatch = line.match(MARKDOWN_ATX_HEADING_PATTERN);

        if(headingMatch) {
            closeMarkdownBlocks(state);

            const level = (headingMatch[1] ?? "#").length;

            const nativeType = MARKDOWN_HEADING_KEYS[level - 1] ?? "# ";

            const content = getMarkdownHeadingContent(line.slice(headingMatch[0].length));

            state.blocks.push({
                variant: getMarkdownDefaultVariant(nativeType),
                inlines: parseStandardInline(content),
                type: "text",
                nativeType,
                content,
                level,
                key
            });

            state.isAfterBlank = false;

            index++;

            continue;
        }

        const paragraph = state.paragraph;

        const setextMatch = paragraph ? line.match(MARKDOWN_SETEXT_UNDERLINE_PATTERN) : null;

        if(paragraph && setextMatch) {
            state.paragraph = null;

            const level = setextMatch[1]?.startsWith("=") ? 1 : 2;

            const nativeType = MARKDOWN_HEADING_KEYS[level - 1] ?? "# ";

            const content = paragraph.lines.join("\n").trim();

            state.blocks.push({
                variant: getMarkdownDefaultVariant(nativeType),
                inlines: parseStandardInline(content),
                key: paragraph.key,
                type: "text",
                nativeType,
                content,
                level
            });

            closeMarkdownList(state);

            index++;

            continue;
        }

        if(MARKDOWN_THEMATIC_BREAK_PATTERN.test(line)) {
            closeMarkdownBlocks(state);

            state.blocks.push({
                nativeType: "---",
                type: "rule",
                content: "",
                key
            });

            state.isAfterBlank = false;

            index++;

            continue;
        }

        if(depth < MARKDOWN_MAX_NESTING && MARKDOWN_BLOCKQUOTE_PATTERN.test(line)) {
            closeMarkdownBlocks(state);

            const quote = collectMarkdownQuote({
                start: index,
                lines
            });

            state.blocks.push({
                variant: getMarkdownDefaultVariant("> "),
                content: quote.innerLines.join("\n"),
                children: parseStandardBlocks({
                    lines: quote.innerLines,
                    depth: depth + 1,
                    offset: key
                }),
                type: "blockquote",
                nativeType: "> ",
                key
            });

            state.isAfterBlank = false;

            index = quote.end;

            continue;
        }

        const listMatch = line.match(MARKDOWN_LIST_ITEM_PATTERN);

        const listContent = listMatch ? listMatch[5] ?? "" : "";

        const listStart = listMatch && listMatch[3] !== undefined ? Number(listMatch[3]) : 1;

        const isListInterrupting = !state.paragraph || (listContent.trim() !== "" && listStart === 1);

        if(listMatch && isListInterrupting) {
            closeMarkdownParagraph(state);

            const isOrdered = listMatch[3] !== undefined;

            const nativeType: ListTypes = isOrdered ? "1. " : `${listMatch[2] ?? "-"} ` as ListTypes;

            const markerIndent = getMarkdownIndent(listMatch[1] ?? "");

            const position = getMarkdownListPosition({
                listStack: state.listStack,
                start: listStart,
                markerIndent,
                nativeType
            });

            const listItem: MarkdownObject = {
                variant: getMarkdownDefaultVariant(nativeType),
                content: listContent.trim(),
                level: position.level,
                indent: markerIndent,
                type: "list",
                nativeType,
                isOrdered,
                key
            };

            if(isOrdered) {
                listItem.order = position.order;
            }

            state.blocks.push(listItem);

            state.listItem = listItem;

            state.isAfterBlank = false;

            index++;

            continue;
        }

        const listItem = state.listItem;

        const isListContinuation = !!listItem && !state.paragraph && (!state.isAfterBlank || getMarkdownIndent(line) >= 2);

        if(MARKDOWN_BREAK_LINE_PATTERN.test(line) && !state.paragraph && !isListContinuation) {
            closeMarkdownList(state);

            state.blocks.push({
                variant: getMarkdownDefaultVariant("<br/>"),
                nativeType: "<br/>",
                type: "space",
                content: "",
                key
            });

            state.isAfterBlank = false;

            index++;

            continue;
        }

        const paragraphLine = getMarkdownParagraphLine(line);

        if(listItem && isListContinuation) {
            listItem.content = `${listItem.content}${state.isAfterBlank ? "\n\n" : "\n"}${paragraphLine}`;

            state.isAfterBlank = false;

            index++;

            continue;
        }

        if(state.paragraph) {
            state.paragraph.lines.push(paragraphLine);
        } else {
            closeMarkdownList(state);

            state.paragraph = {
                lines: [
                    paragraphLine
                ],
                key
            };
        }

        state.isAfterBlank = false;

        index++;
    }

    closeMarkdownParagraph(state);

    state.blocks.forEach((block) => {
        if(block.type !== "list") {
            return;
        }

        block.inlines = parseStandardInline(block.content);
    });

    return state.blocks;
};

const getLegacyMarkdownDetails = (nodes: Array<MarkdownObject>): Array<MarkdownObject> => {
    const listStack: Array<MarkdownListStackItemType> = [];

    nodes.forEach((node) => {
        if(node.type === "text" || node.type === "list" || node.type === "blockquote") {
            node.inlines = parseLegacyInline(node.content);
        }

        if(node.type !== "list") {
            listStack.length = 0;

            return;
        }

        node.level = getMarkdownListPosition({
            nativeType: node.nativeType as ListTypes,
            markerIndent: node.indent ?? 0,
            listStack,
            start: 1
        }).level;
    });

    return nodes;
};

export const getMarkdownListGroups = (items: Array<MarkdownObject>): Array<MarkdownListGroupType> => {
    const groups: Array<MarkdownListGroupType> = [];

    const groupStack: Array<MarkdownListGroupType> = [];

    items.forEach((node) => {
        const level = Math.min(node.level ?? 0, groupStack.length);

        groupStack.length = Math.min(groupStack.length, level + 1);

        const currentGroup = groupStack[level];

        if(currentGroup && currentGroup.nativeType === node.nativeType) {
            currentGroup.items.push({
                groups: [],
                node
            });

            return;
        }

        const group: MarkdownListGroupType = {
            nativeType: node.nativeType,
            items: [
                {
                    groups: [],
                    node
                }
            ],
            level
        };

        const parentItems = level > 0 ? groupStack[level - 1]?.items : undefined;

        const parentItem = parentItems ? parentItems[parentItems.length - 1] : undefined;

        if(parentItem) {
            parentItem.groups.push(group);
        } else {
            groups.push(group);
        }

        groupStack[level] = group;
    });

    return groups;
};

export const parseMarkdown = (rawText: string, syntax: MarkdownSyntax = "standard"): Array<MarkdownObject> => {
    if(syntax === "standard") {
        return parseStandardBlocks({
            lines: rawText.replace(/\r\n?/g, "\n").split("\n"),
            offset: 0
        });
    }

    const lines = rawText.split("\n");
    const skippedIndices: number[] = [];

    return getLegacyMarkdownDetails(lines.map((line, index) => {
        if (skippedIndices.includes(index)) {
            return {
                type: undefined,
                nativeType: "",
                content: "",
                key: index
            };
        }

        if (line.startsWith("# ")) return textMarkdown({
            type: "# ",
            index,
            line
        });

        if (line.startsWith("## ")) return textMarkdown({
            type: "## ",
            index,
            line
        });

        if (line.startsWith("### ")) return textMarkdown({
            type: "### ",
            index,
            line
        });

        if (line.startsWith("#### ")) return textMarkdown({
            type: "#### ",
            index,
            line
        });

        if (line.startsWith("##### ")) return textMarkdown({
            type: "##### ",
            index,
            line
        });

        if (line.startsWith("###### ")) return textMarkdown({
            type: "###### ",
            index,
            line
        });

        if (line.startsWith("<p>")) return textMarkdown({
            type: "<p>",
            index,
            line
        });

        if (line.startsWith("<br/>") || line === "") return enterMarkdown({
            index,
            line
        });

        if (line.startsWith("```")) {
            const returnedData = codeMarkdown({
                skippedIndices,
                index,
                lines
            });

            if(returnedData) return returnedData;
        }

        if (line.startsWith("<center>")) {
            const returnedData = centerMarkdown({
                skippedIndices,
                index,
                lines
            });

            if(returnedData) return returnedData;
        }

        if (line.startsWith("<right>")) {
            const returnedData = rightMarkdown({
                skippedIndices,
                index,
                lines
            });

            if(returnedData) return returnedData;
        }

        if (line.startsWith("<left>")) {
            const returnedData = leftMarkdown({
                skippedIndices,
                index,
                lines
            });

            if(returnedData) return returnedData;
        }

        const linkStartRegex = /^<link\s+href=["'](.*?)["']>/;

        if (linkStartRegex.test(line)) {
            const returnedData = linkMarkdown({
                linkStartRegex,
                skippedIndices,
                index,
                lines,
                line
            });

            if(returnedData) return returnedData;
        }

        if (line.startsWith("<<")) {
            const returnedData = blockquoteMarkdown({
                skippedIndices,
                index,
                lines
            });

            if(returnedData) return returnedData;
        }

        if(LEGACY_IMAGE_PATTERN.test(line)) return imageMarkdown({
            index,
            line
        });

        const listMatch = line.match(/^(\s*)(?:\*|-)\s(.*)/);

        if (listMatch) {
            const isStarList = line.trim().startsWith("*");
            return listMarkdown({
                indent: listMatch[1]?.length ?? 0,
                line: listMatch[2] ?? "",
                isStarList,
                index
            });
        }

        return {
            variant: DEFAULT_VARIANTS["<p>"],
            nativeType: "<p>",
            content: line,
            type: "text",
            key: index
        };
    }));
};

export const handleMarkdownLinkPress = ({
    onLinkPress,
    event,
    url
}: {
    onLinkPress?: (url: string) => void;
    event?: GestureResponderEvent;
    url: string;
}) => {
    if(onLinkPress) {
        if(Platform.OS === "web") {
            event?.preventDefault();
        }

        onLinkPress(url);

        return;
    }

    if(Platform.OS === "web") {
        return;
    }

    Linking.openURL(url).catch(() => undefined);
};
